"use client";

import { useState } from "react";

import { ContactApiError, sendContactMessage } from "@/lib/api/contact";

/**
 * About-page contact form (B-166, 2026-09-02). Owner-approved copy
 * verbatim; UX pattern aligned with the Inqvey contact page (name +
 * email row, subject dropdown, message, success card with "Send
 * another message"), restyled to the /retailers palette: literal hex
 * colors on purpose - the page commits to a single light look in both
 * themes (do not "fix" the literals back to tokens).
 *
 * The `website` input is the honeypot: visually hidden, out of the tab
 * order, never announced to screen readers. The api silently drops
 * submissions that fill it. Messages are stored server-side and
 * forwarded to the founder inbox.
 */

const INK = "#171310";
const MUTED = "#565049";
const FAINT = "#756E62";
const BLUE = "#2563EB";
const BORDER = "#DCE6F5";

const TOPICS: Array<{ value: string; label: string }> = [
  { value: "general", label: "General question" },
  { value: "search_help", label: "Help with a search or a price watch" },
  { value: "wrong_price", label: "Report a wrong price" },
  { value: "partnership", label: "Retailer partnership" },
  { value: "media", label: "Media enquiry" },
  { value: "other", label: "Other" },
];

const fieldCls = "w-full rounded-lg px-3 py-2 text-base outline-none";
const fieldStyle: React.CSSProperties = {
  background: "#FFFFFF",
  border: `1px solid ${BORDER}`,
  color: INK,
};

// Chevron for the select, drawn inline so no icon library is needed.
const chevron =
  "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23565049' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")";

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-sm font-medium" style={{ color: INK }}>
      {children}
    </span>
  );
}

export function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("general");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await sendContactMessage({
        name: name.trim(),
        email: email.trim(),
        topic,
        message: message.trim(),
        website: website || undefined,
      });
      setDone(true);
      setSubmitting(false);
    } catch (err) {
      if (err instanceof ContactApiError && err.status === 429) {
        setError("Too many attempts. Please wait a minute and try again.");
      } else if (err instanceof ContactApiError && err.status === 422) {
        setError("Please check the email address and try again.");
      } else {
        setError("Something went wrong. Please try again in a moment.");
      }
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div
        className="rounded-xl p-8 text-center"
        style={{ background: "#FAFCFE", border: `1px solid ${BORDER}` }}
        role="status"
      >
        <p className="text-lg font-semibold" style={{ color: INK }}>
          Message sent.
        </p>
        <p className="mt-2 text-sm" style={{ color: MUTED }}>
          Thanks for reaching out. We&apos;ll get back to you within 24 hours.
        </p>
        <button
          type="button"
          className="mt-4 text-sm font-semibold"
          style={{ color: BLUE }}
          onClick={() => {
            setDone(false);
            setName("");
            setEmail("");
            setTopic("general");
            setMessage("");
          }}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <Label>Name</Label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoComplete="name"
            placeholder="Your name"
            className={fieldCls}
            style={fieldStyle}
            onFocus={(e) => (e.currentTarget.style.borderColor = BLUE)}
            onBlur={(e) => (e.currentTarget.style.borderColor = BORDER)}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <Label>Email</Label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            inputMode="email"
            placeholder="you@email.com"
            className={fieldCls}
            style={fieldStyle}
            onFocus={(e) => (e.currentTarget.style.borderColor = BLUE)}
            onBlur={(e) => (e.currentTarget.style.borderColor = BORDER)}
          />
        </label>
      </div>

      <label className="flex flex-col gap-1.5">
        <Label>What&apos;s this about?</Label>
        <select
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className={`${fieldCls} cursor-pointer appearance-none`}
          style={{
            ...fieldStyle,
            backgroundImage: chevron,
            backgroundPosition: "right 0.75rem center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "1.25em 1.25em",
            paddingRight: "2.5rem",
          }}
        >
          {TOPICS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1.5">
        <Label>Message</Label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          rows={6}
          placeholder="Tell us what's on your mind."
          className={`${fieldCls} resize-none`}
          style={fieldStyle}
          onFocus={(e) => (e.currentTarget.style.borderColor = BLUE)}
          onBlur={(e) => (e.currentTarget.style.borderColor = BORDER)}
        />
      </label>

      {/* Honeypot: visually hidden, out of tab order, hidden from AT. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
        }}
      >
        <label>
          Website
          <input
            type="text"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </label>
      </div>

      {error ? (
        <p className="text-sm" style={{ color: "#C23A24" }} role="alert">
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={submitting || !name.trim() || !email.trim() || !message.trim()}
        className="rounded-lg px-5 py-2.5 text-base font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ background: BLUE, color: "#FFFFFF" }}
      >
        {submitting ? "Sending…" : "Send message"}
      </button>

      <p className="text-center text-xs" style={{ color: FAINT }}>
        Sydney, Australia
      </p>
    </form>
  );
}
